/* eslint-disable camelcase */
import crypto from 'crypto';

import moment from 'moment';

import { expectdCardConfig, pastCardConfig, scheduledCardConfig } from './flightpath/appointmentsCardConfig';
import { appointmentCardTypes, excludedTreatmentStatuses } from './flightpath/config';
import { dateConverter } from './utils';

const { NO_SHOW_VISIT_OVERDUE,
  NOT_SCHEDULED,
  VISIT_OVERDUE,
  CONFIRMATION,
  SCHEDULED,
  DEFAULT } = appointmentCardTypes;

export const setTimeStampForUpload = () => {
  const date = new Date();
  const SS = String(date.getMilliseconds()).padStart(3, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const MM = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${yyyy}${MM}${dd}${hh}${mm}${ss}${SS}`;
};

export const setFileNameForUpload = (fileName) => {
  const maxLength = 242;
  if (fileName.length <= maxLength) {
    return fileName;
  }
  return fileName.slice(-maxLength);
};

export const setUploadKey = (fileName, treatmentId) => {
  const timestamp = setTimeStampForUpload();
  const trimmedName = setFileNameForUpload(fileName);
  const key = `images/treatments/${treatmentId}/my_progress/${timestamp}_${trimmedName}`;
  return key || '';
};

export const mapTreatmentDetailsToAppointmentCard = (details) => ({
  id: details.appointment_id,
  appointmentDate: details.actual_appointment_date,
  expectedDate: details.expected_appointment_date,
  clinicName: details.doctor_clinic,
  type: details.appointment_type,
  status: details.appointment_stat,
  set: details.before_set_number,
  typeLabel: details.appointment_type_label,
  appointmentOrder: details.appointment_order
});

export const formatTimeAndDate = (timeStamp, datesAndMonths) => {
  const formattedValue = moment(timeStamp).format('YYYY-MM-DD hh:mm A');
  const dateArray = formattedValue.split(' ');
  const { date, dayShortName, monthShortName } = dateConverter.convertToDateAndMonth(dateArray[0], datesAndMonths);
  return { date, dayShortName, monthShortName, time: `${dateArray[1]}${dateArray[2]}` };
};

export const mapDataForIntercom = (userData, treatment, lang) => {
  const userHMAC = getHMACforIntercomChat(userData.id);
  const treatmentLink = treatment
    ? `${process.env.NEXT_PUBLIC_CONTROL_PANEL}/#/treatment/edit/${treatment.id}/view`
    : '';
  const data = {
    email: userData.email,
    userId: userData.id,
    userHash: userHMAC,
    name: userData.firstName + userData.lastName,
    phone: userData.phoneNumber,
    languageOverride: lang,
    customAttributes: {
      hide_default_launcher: true,
      ...(treatment && {
        salesforce_owner_name:
      treatment?.employee?.first_name + treatment?.employee?.last_name || '',
        treatment_link: treatmentLink,
        doctor_name: treatment?.doctor?.first_name + treatment?.doctor?.last_name || '',
        clinic_name: treatment?.doctor?.clinic || '',
        opportunity_source: treatment?.opportunity_source
      })
    }
  };
  return data;
};

export const returnScheduledCardProps = (details) => {
  const { actual_appointment_date, appointment_status } = details;
  let type = SCHEDULED;
  const twodaysBeforeActualDate = moment(actual_appointment_date).subtract(2, 'days');
  const isTwoDaysBeforeActualDate = moment().isBetween(
    twodaysBeforeActualDate, moment(actual_appointment_date), undefined, []
  );
  if (isTwoDaysBeforeActualDate && appointment_status !== 'CONFIRMED_BY_PATIENT_AND_CLINIC') {
    type = CONFIRMATION;
  }
  return scheduledCardConfig[type];
};

export const returnPastCardProps = (details) => {
  if (Object.keys(pastCardConfig).includes(details.appointment_status)) {
    return pastCardConfig[details.appointment_status];
  }
  if (details.appointment_status === 'NONE') {
    const expectedCardprops = returnExpectedCardProps(details);
    return expectedCardprops;
  }
  const scheduledCardProps = returnScheduledCardProps(details);
  return scheduledCardProps;
};

export const returnExpectedCardProps = (details) => {
  const type = getExpectedCardType(details);
  return expectdCardConfig[type];
};

export const getExpectedCardType = (details) => {
  const { expected_appointment_date, appointment_action, reschedule_reason = '' } = details;
  const fourDaysAfterExpectedAppointmentDate = moment(expected_appointment_date).add(4, 'days');
  let type = '';
  if (appointment_action === 'SCHEDULE_APPOINTMENT' && reschedule_reason === 'NO_SHOW') {
    type = NO_SHOW_VISIT_OVERDUE;
  } else if (moment().isAfter(fourDaysAfterExpectedAppointmentDate)) {
    type = VISIT_OVERDUE;
  } else if (moment().isAfter(expected_appointment_date)) {
    type = NOT_SCHEDULED;
  } else type = DEFAULT;
  return type;
};

export const isValidPatient = (treatment) => {
  let isValidUser = false;
  let redirectToTreatmentPlan = false;
  if (treatment.length === 1 && !excludedTreatmentStatuses.includes(treatment[0].treatment_status)) {
    isValidUser = true;
  } else if (treatment.length === 1) {
    redirectToTreatmentPlan = true;
    isValidUser = false;
  } else if (treatment.length > 1) isValidUser = true;
  return { isValidUser, redirectToTreatmentPlan };
};

export const downloadConsentForm = async (file) => {
  try {
    const pdfUrl = file;
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(file);
      return;
    }
    const filePath = pdfUrl.split('?');
    const fileName = filePath[0].split('/').pop();
    const response = await fetch(pdfUrl);
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `consent_form.${fileName.split('.').pop()}`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (err) {
    console.error('Error fetching PDF:', err);
  }
};

export const getHMACforIntercomChat = (userId) => {
  const secretKey = process.env.NEXT_PUBLIC_INTERCOM_IDENTITY_VERIFICATION_KEY;
  const userIdentifier = userId.toString();
  const hash = crypto.createHmac('sha256', secretKey).update(userIdentifier).digest('hex');
  return hash;
};

export const formatDateToDDMMYYYY = (date) => moment(date).format('DD/MM/YYYY');

export const getCollectionLocation = ({
  clinicName,
  clinicStreet,
  clinicCity,
  clinicState,
  clinicCountry,
  clinicZip
}) => `${clinicName || ' '} ${clinicName ? '-' : ''} ${clinicStreet || ' '} \n 
        ${clinicCity || ''}  ${clinicState || ''}  ${clinicCountry || ''} \n
        ${clinicZip || ''} `;

export const getOrdinalSuffixOfNumber = (number) => {
  if (number < 0) return '';
  if (number % 100 >= 10 && number % 100 <= 20) {
    return `${number}th`;
  }
  switch (number % 10) {
    case 1: return `${number}st`;
    case 2: return `${number}nd`;
    case 3: return `${number}rd`;
    default: return `${number}th`;
  }
};

export const getRetainerJourneyDeeplink = (userId) => `https://zenyum.onelink.me/4taf?pid=Custom&deep_link_value=retainer-journey-userId=${userId}&af_dp=zenyum%3A%2F%2F&af_xp=custom&c=Retainer%20Journe`;

export const padZeroToStart = (number) => String(number).padStart(2, '0');

export const isOnboardedDay = (treatment) => treatment?.opportunity_type === 'AFTER_SALES'
&& moment(new Date()).isSame(moment(treatment?.start_date), 'day')
&& moment(new Date()).isAfter(moment(treatment?.start_date), 'minutes');

export const SCREEN_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  LARGE: 'large'
};

export const removeQueryParam = (router, paramName) => {
  const query = router?.query;
  if (!query || !paramName) return;

  const updatedQuery = { ...query };
  delete updatedQuery[paramName];

  router.push(
    {
      pathname: router.pathname,
      query: updatedQuery
    },
    undefined, // No custom URL is to be displayed
    { shallow: true }
  );
};

export const formatAmount = (price, showDecimals = true) => {
  if (!price && price !== 0) return showDecimals ? '0.00' : '0';

  if (showDecimals) {
    return price.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }
  return price.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};
